import { createMemo, Show, splitProps, type JSX } from 'solid-js';
import ItemStackView from '../ItemStackView';
import { fromInventorySlot } from '../../services/itemRefAdapters';
import { sameItemStack, type ItemStack } from '../../models/itemStack';
import { isInventorySlotEmpty, type InventorySlot } from '../../models/inventorySlot';
import styles from './WindowSlot.module.css';

// The Minecraft window tile, as opposed to the storage browse tile in ItemSlotGrid: fixed 44px on a
// fixed nine-wide row, because it stands in for a real game GUI rather than filling a panel. The live
// inventory and the dump panel drive different interactions on the same tile, so the state flags are
// named props and every other attribute passes straight through to the element.
export interface WindowSlotProps extends JSX.HTMLAttributes<HTMLDivElement> {
    item: InventorySlot | undefined;
    active?: boolean;
    interactive?: boolean;
    dragging?: boolean;
    dropTarget?: boolean;
    selected?: boolean;
    toggleable?: boolean;
}

export default function WindowSlot(props: WindowSlotProps) {
    const [own, rest] = splitProps(props, [
        'item', 'active', 'interactive', 'dragging', 'dropTarget', 'selected', 'toggleable', 'class',
    ]);
    const empty = () => isInventorySlotEmpty(own.item);

    // An inventory push rebuilds every slot object, so diffing on content is what keeps one changed
    // stack from tearing down and re-rendering the whole grid of icons, tooltips and skull heads.
    const stack = createMemo<ItemStack | null>(
        () => (empty() ? null : fromInventorySlot(own.item!)),
        null,
        { equals: sameItemStack },
    );

    const classes = () => [
        styles.slot,
        empty() ? styles.empty : '',
        own.active ? styles.active : '',
        own.interactive ? styles.interactive : '',
        own.dragging ? styles.dragging : '',
        own.dropTarget ? styles.dropTarget : '',
        own.toggleable ? styles.toggleable : '',
        own.selected ? styles.selected : '',
        own.class ?? '',
    ].filter(Boolean).join(' ');

    return (
        <div {...rest} class={classes()}>
            <Show when={stack()}>
                {(s) => <ItemStackView stack={s()} size={44} bare showDurability showTooltip />}
            </Show>
        </div>
    );
}

export function WindowSlotRow(props: { armor?: boolean; children: JSX.Element }) {
    return <div class={props.armor ? styles.armorRow : styles.grid}>{props.children}</div>;
}
